import { create } from 'zustand';

import Const from '../Constants';

export const useAgentsStore = create((set, get) => ({
  globalAgents: [],
  localAgents: [],
  currentAgent: null,
  runningTaskIds: [],

  setCurrentAgent: (agent) => set({ currentAgent: agent }),

  getAllAgents: () => [...get().globalAgents, ...get().localAgents],

  loadAgents: async (auth) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}${Const.sAgentsPath}`, {
        headers: new Headers({ Authorization: idToken }),
      });
      const data = await response.json();
      const globalAgents = Array.isArray(data?.global) ? data.global : [];
      const localAgents = Array.isArray(data?.local) ? data.local : [];
      set((state) => ({
        globalAgents,
        localAgents,
        // First-run: show an example agent in the editor instead of the
        // placeholder. Never overrides an existing selection.
        currentAgent: state.currentAgent ?? globalAgents[0] ?? localAgents[0] ?? null,
      }));
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  },

  addAgent: async (auth, sourceAgentId = null) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const body = sourceAgentId
        ? { sourceAgentId }
        : {
          name: 'New Agent',
          role: '',
          briefing: '',
          model: Const.sDefaultAgentModel || 'claude-sonnet-4-6',
          avatar: null,
        };
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}${Const.sAgentsPath}`, {
        method: Const.HttpPost,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson,
        }),
        body: JSON.stringify(body),
      });
      const newAgent = await response.json();
      set((state) => ({ localAgents: [...state.localAgents, newAgent], currentAgent: newAgent }));
      return newAgent;
    } catch (error) {
      console.error('Error adding agent:', error);
    }
  },

  updateAgent: async (auth, agentId, name, role, briefing, model, avatar) => {
    // Optimistic update (local agents only — globals are read-only)
    set((state) => ({
      localAgents: state.localAgents.map((a) =>
        a.id === agentId
          ? { ...a, ...(name !== null && { name }), ...(role !== null && { role }), ...(briefing !== null && { briefing }), ...(model !== null && { model }), ...(avatar !== undefined && avatar !== null && { avatar }) }
          : a
      ),
      currentAgent: state.currentAgent?.id === agentId
        ? { ...state.currentAgent, ...(name !== null && { name }), ...(role !== null && { role }), ...(briefing !== null && { briefing }), ...(model !== null && { model }), ...(avatar !== undefined && avatar !== null && { avatar }) }
        : state.currentAgent,
    }));

    try {
      const idToken = await auth.currentUser.getIdToken();
      const body = { agentId };
      if (name !== null) body.name = name;
      if (role !== null) body.role = role;
      if (briefing !== null) body.briefing = briefing;
      if (model !== null) body.model = model;
      if (avatar !== null && avatar !== undefined) body.avatar = avatar;

      await fetch(`${process.env.REACT_APP_BASE_URL}${Const.sAgentsPath}`, {
        method: Const.HttpPatch,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson,
        }),
        body: JSON.stringify(body),
      });
    } catch (error) {
      console.error('Error updating agent:', error);
      get().loadAgents(auth);
    }
  },

  deleteAgent: async (auth, agentId) => {
    // Optimistic remove (local agents only)
    set((state) => ({
      localAgents: state.localAgents.filter((a) => a.id !== agentId),
      currentAgent: state.currentAgent?.id === agentId ? null : state.currentAgent,
    }));

    try {
      const idToken = await auth.currentUser.getIdToken();
      await fetch(`${process.env.REACT_APP_BASE_URL}${Const.sAgentsPath}`, {
        method: Const.HttpDelete,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson,
        }),
        body: JSON.stringify({ agentId }),
      });
    } catch (error) {
      console.error('Error deleting agent:', error);
      get().loadAgents(auth);
    }
  },

  runAgent: async (auth, taskId, agentId) => {
    set((state) => ({ runningTaskIds: [...state.runningTaskIds, taskId] }));

    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}${Const.sAgentsRunPath}`, {
        method: Const.HttpPost,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson,
        }),
        body: JSON.stringify({ agentId, taskId }),
      });
      const output = await response.json();
      set((state) => ({
        runningTaskIds: state.runningTaskIds.filter((id) => id !== taskId),
      }));
      return output;
    } catch (error) {
      console.error('Error running agent:', error);
      set((state) => ({ runningTaskIds: state.runningTaskIds.filter((id) => id !== taskId) }));
    }
  },

}));
